import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import React, { useContext, useEffect, useState } from 'react';
import {
    Box, Button
} from '@mui/material';
import { useGridApiRef, GridCellEditStopReasons, gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { useAbortController } from '../../contexts/AbortControllerContext';
import ProjectContext from '../../contexts/ProjectContext';
import FullDataGrid from '../datagrid/FullDataGrid';
import SyncConfigMenuBar from '../menubar/SyncConfigMenuBar';
import TextEditor from '../custom/TextEditor';
import {useFetch} from '../../components/auth/useFetch';



// In milliseconds
let minimumLoadingAnimationDuration = 2000;

const MAX_CONFIG_NAME_LEN = 31

    // Datagrid column Definition for Metadata in DB
    const metadataColumns = [
        {
            field: 'metadataName',
            headerName: 'Available Metadata',
            flex: 1
        },
        {
            field: 'count',
            headerName: 'Count',
            flex: 1
        },
    ]


export default function SyncConfig(props) {

    const {
        deleteConfig,
        deliverable,
        deliverableConfig,
        exportType,
        menuRef,
        metadataRef,
        openMenu,
        setDeliverableConfig,
        setMenuContent,
        submitConfig,
        undoConfig,
        savedUpdate
    } = props
    const { abortSimilarGroupRequests, registerController, unregisterController } = useAbortController()
    const projectContext = useContext(ProjectContext)
    const { enqueueSnackbar } = useSnackbar()

    const [configRows, setConfigRows] = useState([])
    const [metadataRows, setMetadataRows] = useState([])
    const [customRows, setCustomRows] = useState([])
    const [useTextEditor, setUseTextEditor] = useState(false)

    const configRef = useGridApiRef();
    const fetchWithAuth = useFetch()

    useEffect(() => {
        if(deliverable?.type === exportType)
        {
            retrieveMetadata()
        }
    }, [deliverable, savedUpdate])

    // Datagrid column Definition for ALE Config
    const configColumns = [
        {
            field: 'metadataName',
            sortable: false,
            headerName: 'Konsol Field',
            flex: 1
        },
        {
            field: 'configName',
            sortable: false,
            headerName: 'Avid Column',
            flex: 1,
            editable: true ,
            valueParser: (value) => (value ?? '').toString().slice(0, MAX_CONFIG_NAME_LEN),
            preProcessEditCellProps: (params) => {
                const raw = (params.props.value ?? '').toString();
                let error = false
                const duplicates = configRows.find(row => row?.id !== params.row?.id && row?.configName === raw)
                if (raw.length > MAX_CONFIG_NAME_LEN) {
                   error = true 
                   enqueueSnackbar(`Config name can not exceed ${MAX_CONFIG_NAME_LEN} characters`, { variant: 'error' });
                }
                else if (duplicates) {
                    error = true
                    enqueueSnackbar(`Duplicate config name found for Konsol Field: ${duplicates.metadataName}`, { variant: 'error' });
                }
                return {
                    ...params.props,
                    error: error
                }
            }
        },
        {
            field: 'sync_type',
            sortable: false,
            headerName: 'Sync Type',
            flex: 2,
            renderCell:(params) => {
                return (
                    <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={params.row.sync_type || 'both'}
                    onChange={(event, newValue) => {
                        if (newValue !== null) {
                            let tempConfigRows = [...configRows]
                            tempConfigRows[params.row.id].sync_type = newValue
                            setConfigRows([...tempConfigRows])
                        }
                    }}
                    sx={{
                        '& .MuiToggleButton-root': {
                            bgcolor: 'rgb(88, 114, 96)',
                            color: 'white',
                            outline: '1px solid black',
                        },
                        '& .MuiToggleButton-root:hover': {
                            bgcolor: 'rgb(114, 163, 126)',
                            color: 'white',
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                            bgcolor: 'rgb(114, 163, 126)',
                            color: 'white',
                        },
                        '& .MuiToggleButton-root.Mui-selected:hover': {
                            bgcolor: 'rgb(114, 163, 126)',
                            color: 'white',
                        },
                    }}
                    >
                        <ToggleButton value="fromAvid" aria-label="centered">
                            From Avid
                        </ToggleButton>
                        <ToggleButton value="toAvid" aria-label="right aligned">
                            To Avid
                        </ToggleButton>
                        <ToggleButton value="both" aria-label="left aligned">
                            Both
                        </ToggleButton>
                    </ToggleButtonGroup>
                
                )
                
            }
        }]





    const retrieveMetadata = () => {
        abortSimilarGroupRequests(`deliverable.config.get`)
        const dbConfigController = registerController(`deliverable.config.get.${deliverable.type}.${deliverable.name}`)
        let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/dbconfig/?config_name=${deliverable.name}&config_type=${deliverable.type}`
        fetchWithAuth(url, {
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
            signal: dbConfigController.signal
        })
        .then((response) => response.json())
        .then((res) => {
            setDeliverableConfig(res)
            
            let custom = Object.entries(res?.args?.customColumn || {}).map(
                ([metadataName, customString], index) => ({
                    id: index,
                    metadataName,
                    customString
                })
            )
            setCustomRows(custom)

            let id = 0;
            const config = Object.entries(res.data).flatMap(
                ([konsolKey, configDetail]) => 
                {
                    return({
                        "id": id++,                        
                        "metadataName": konsolKey,
                        "configName": configDetail['avid_key'],
                        "sync_type": configDetail['sync_type']
                    })
                }
            )
            setConfigRows(config)
            return config
        })
        .catch((e) => {
            if(e.name !== 'AbortError'){
                console.error(e);
                setDeliverableConfig({})
                setConfigRows([])
                setCustomRows([])
            }
        })
        .then((config) => {
            return config.reduce((acc, obj) => {
                acc[obj.metadataName] = (acc[obj.metadataName] || 0) + 1;
                return acc;
            }, {})
        })
        .catch((error) => {
            return 0
        })
        .then((count) => {
            const metadataController = registerController(`deliverable.config.get.metadata`)
            let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/metadata`
            fetchWithAuth(url, {
                headers:{
                    'X-Project-ID': projectContext?.project_db
                },
                signal: metadataController.signal
            })
                .then((response) => response.json())
                .then((res) => {
                    setMetadataRows(() => {
                        // Filter out any metadata that starts with 'Avid-' - otherwise users could create items that would sync as Avid-Avid-keyname, or further recursion
                        res = res.filter(item => !item.startsWith('Avid-'))
                        return res.map((item, index) => {
                            return { 
                                id: index, 
                                metadataName: item,
                                count: count[item] !== undefined ? count[item] : 0
                            }
                        })
                    })
                })
                .catch(e => {
                    if(e.name !== 'AbortError'){
                        console.error(e);
                    }
                })
                .finally(() => unregisterController(metadataController, `deliverable.config.get.metadata`))
        })
        .catch(e => {
            if(e.name !== 'AbortError'){
                console.error(e);
            }
        })
        .finally(() => unregisterController(dbConfigController, `deliverable.config.get.${deliverable.type}.${deliverable.name}`))
    }

    const addConfigRowOnClick = (e) => {
        const sortedRows = configRef.current.getSortedRowIds()
        let config_name = e.row.metadataName.slice(0, MAX_CONFIG_NAME_LEN)
        const row = {
            id: sortedRows.length > 0 ? sortedRows[sortedRows.length - 1] + 1 : 0,
            metadataName: e.row.metadataName,
            configName: config_name,
            sync_type: 'both' // default sync type when adding a new row, can be changed by user
        }
        metadataRef.current.updateRows([{...e.row, count: ++e.row.count}])
        setConfigRows((prev) => {
            return [...prev, row]
        })
    }

    const addVisibleConfigRows = () => {
        const configRows = configRef.current.getSortedRowIds()
        let id = configRows.length > 0 ? configRows[configRows.length - 1] + 1 : 0

        metadataRef.current.updateRows(
            gridFilteredSortedRowEntriesSelector(metadataRef)
            .map((item) => {
                return {...item.model, count: ++item.model.count}
            })
        )

        setConfigRows((prev) => {
            return [...prev, ...gridFilteredSortedRowEntriesSelector(metadataRef)
                .map((item) => {
                    return {
                        id: id++,
                        metadataName: item.model.metadataName,
                        configName: item.model.metadataName
                    }
                })
            ]
        })
    }

    const addCustomRow = (konsol, avid) => {
        const sortedRows = configRef.current.getSortedRowIds()
        const row = {
            id: sortedRows.length > 0 ? sortedRows[sortedRows.length - 1] + 1 : 0,
            metadataName: konsol,
            configName: avid,
            sync_type: 'both' // default sync type when adding a new row, can be changed by user
        }
        setConfigRows((prev) => {
            return [...prev, row]
        })
    }


    const deleteConfigRows = (row, e) => {
        //If a delete/backspace button is pressed
        //46 = Delete Key
        //8 = Backspace Key
        if(row.cellMode === "view" && (e.keyCode === 46 || e.keyCode === 8)){
            const rowsToDelete = [...configRef.current.getSelectedRows().keys()]
            setConfigRows((prev) => {
                const reOrderRows = [...prev].filter((_, index) => !rowsToDelete.includes(index))
                reOrderIDs(reOrderRows, Math.min(...rowsToDelete))
                return reOrderRows
            })
        }
    }

    const handleCellEditStop = (params, e) => {
        if (params.reason === GridCellEditStopReasons.cellFocusOut) {
            e.defaultMuiPrevented = true;
        }
        else{
            params.row.configName = (e.target.value || '').slice(0, MAX_CONFIG_NAME_LEN);
        }
    }

    const handleRowChange = (params) => {
        setConfigRows((prev) => {
            const reOrderRows = [...prev]
            const row = reOrderRows.splice(params.oldIndex, 1)[0]
            reOrderRows.splice(params.targetIndex, 0, row)
            reOrderIDs(reOrderRows, Math.min(params.oldIndex, params.targetIndex))
            return reOrderRows
        })
        configRef.current.setRowSelectionModel([])
    }

    const reOrderIDs = (rowArr, startIndex) => {
        return rowArr.slice(startIndex).forEach((item, index) => {
            item.id = startIndex + index
        })
    }

    

    const handleArgsChange = (value) => {
        setDeliverableConfig(value)
    };

    return (
        <React.Fragment>
            {deliverable?.type === exportType && (
                <>
                <Box
                    display='flex'
                    flexDirection={'row'} 
                    flex={0.35}
                    height='100%'
                    width='35%'                    
                    // border='1px solid red'
                >
                    <FullDataGrid
                        apiRef={metadataRef}
                        rows={metadataRows}
                        columns={metadataColumns}
                        onRowClick={(e) => addConfigRowOnClick(e)}
                        disableSelection
                    />
                </Box>
                <Box
                    display='flex'
                    flexDirection={'row'} 
                    flex={0.65}
                    height='100%'
                    width='65%'
                    // border='1px solid red'
                >
                    {
                        useTextEditor 
                        ?
                            <TextEditor
                                configText={JSON.stringify(deliverableConfig)}
                            />
                        :
                            <FullDataGrid
                                // loading={loading}
                                apiRef={configRef}
                                rows={configRows}
                                columns={configColumns}
                                rowReordering={true}
                                onRowOrderChange={handleRowChange}
                                onCellEditStop={handleCellEditStop}
                                onCellKeyDown={deleteConfigRows}
                            />
                    }
                </Box>
                <SyncConfigMenuBar
                    addVisibleConfigRows={addVisibleConfigRows}
                    config={deliverableConfig}
                    configRef={configRef}
                    customRows={customRows}
                    deleteConfig={deleteConfig}
                    handleChange={handleArgsChange}
                    menuRef={menuRef}
                    openMenu={openMenu}
                    setConfigRows={setConfigRows}
                    setCustomRows={setCustomRows}
                    setMenuContent={setMenuContent}
                    submitConfig={submitConfig}
                    setUseTextEditor={setUseTextEditor}
                    undoConfig={undoConfig}
                    addCustomRow={addCustomRow}
                />
                </>
            )}
        </React.Fragment>
    )
}
